/**
 * WorkflowParameterSidebar
 *
 * Shell for the workflow parameter sidebar: owns `open` so antd drives the
 * open/close transition, and nothing else. `destroyOnHidden` unmounts
 * `WorkflowParameterContent`, so the ~40 hooks it holds — the form store, the
 * email template context, the PDF and XLSX attachment builders, the source form
 * version — exist only while the sidebar is on screen.
 *
 * `workflow` is nullable because callers now keep this mounted and pass `null`
 * once the sidebar closes. The last non-null workflow is latched so the title
 * and body survive the exit animation rather than blanking halfway through it;
 * `destroyOnHidden` discards the body once the Drawer is actually closed — which
 * is why the drag width lives in `uiState`, a store that outlives this component
 * entirely, and not in the Drawer. `drawerDraggerStyle` makes the handle visible.
 */

import { FC, useRef } from 'react';
import { Drawer, theme } from 'antd';
import { useSnapshot } from 'valtio';
import type { ComponentProps } from 'react';
import type { Workflow } from '#types';
import { drawerDraggerStyle } from '@/components/atoms/drawerDraggerStyle';
import {
  panelMaxWidth,
  panelViewportClamp,
  setPanelWidth,
  uiState,
} from '../../store/uiState';
import WorkflowParameterContent from './WorkflowParameterContent';

type WorkflowParameterSidebarProps = Omit<
  ComponentProps<typeof WorkflowParameterContent>,
  'workflow'
> & {
  workflow: Workflow | null;
  open: boolean;
};

const WorkflowParameterSidebar: FC<WorkflowParameterSidebarProps> = ({
  workflow,
  open,
  ...contentProps
}) => {
  const { token } = theme.useToken();
  const { width } = useSnapshot(uiState.workflowParameterSidebar, { sync: true });
  const latchedWorkflow = useRef<Workflow | null>(null);
  if (workflow) latchedWorkflow.current = workflow;
  const activeWorkflow = workflow ?? latchedWorkflow.current;

  return (
    <Drawer
      title={activeWorkflow?.description?.shortLabel || 'Workflow'}
      placement="right"
      open={open}
      onClose={contentProps.onCancel}
      destroyOnHidden
      size={width}
      maxSize={panelMaxWidth('workflowParameterSidebar')}
      resizable={{ onResize: (next) => setPanelWidth('workflowParameterSidebar', next) }}
      styles={{
        header: { padding: token.padding },
        body: { padding: token.padding },
        dragger: drawerDraggerStyle(token),
        wrapper: { maxWidth: panelViewportClamp('workflowParameterSidebar') },
      }}
    >
      {activeWorkflow && <WorkflowParameterContent {...contentProps} workflow={activeWorkflow} />}
    </Drawer>
  );
};

export default WorkflowParameterSidebar;
